import { useEffect, useState } from 'react'
import { cn } from '@/shared/lib'
import { designTokens } from '@/shared/lib/design-tokens'
import { VideosShellHeader } from './videos-shell-header'
import { VideosShellSidebar } from './videos-shell-sidebar'
import type {
  VideosShellSidebarBundle,
  VideosShellSidebarCategory,
  VideosShellSidebarFilterSection,
  VideosShellSidebarShortcut,
} from './videos-shell-sidebar'

export type VideosShellProps = {
  activeNavigationItem?: 'home' | 'bookmarks' | 'subscription'
  onNavigationSelect?: (itemId: 'home' | 'bookmarks' | 'subscription') => void
  currentPathname?: string
  shortcuts?: VideosShellSidebarShortcut[]
  className?: string
  fullHeight?: boolean
  showSearchControl?: boolean
  searchValue?: string
  onSearchValueChange?: (value: string) => void
  categories?: VideosShellSidebarCategory[]
  selectedCategorySlug?: string | null
  onCategorySelect?: (categorySlug: string | null) => void
  bundles?: VideosShellSidebarBundle[]
  selectedBundleId?: string | null
  onBundleSelect?: (bundleId: string | null) => void
  filterSections?: VideosShellSidebarFilterSection[]
  onFilterSelectionChange?: (selectedByFilterId: Record<string, string[]>) => void
  onResetFilters?: () => void
  onLogout?: () => void
  showFilterControls?: boolean
  children: React.ReactNode
}

export function VideosShell({
  activeNavigationItem = 'home',
  onNavigationSelect,
  currentPathname,
  shortcuts,
  className,
  fullHeight = true,
  showSearchControl = true,
  searchValue,
  onSearchValueChange,
  categories,
  selectedCategorySlug,
  onCategorySelect,
  bundles,
  selectedBundleId,
  onBundleSelect,
  filterSections,
  onFilterSelectionChange,
  onResetFilters,
  onLogout,
  showFilterControls = true,
  children,
}: VideosShellProps) {
  const [isDesktopFilterPanelOpen, setIsDesktopFilterPanelOpen] = useState(false)
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return true
    }

    return window.matchMedia('(min-width: 1024px)').matches
  })
  const [isDesktopSidebarExpanded, setIsDesktopSidebarExpanded] = useState(false)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [isMobileFilterPanelOpen, setIsMobileFilterPanelOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)')
    const handleViewportChange = () => {
      const nextIsDesktopViewport = mediaQuery.matches
      setIsDesktopViewport(nextIsDesktopViewport)

      if (nextIsDesktopViewport) {
        setIsMobileSidebarOpen(false)
        setIsMobileFilterPanelOpen(false)
      } else {
        setIsDesktopSidebarExpanded(false)
        setIsDesktopFilterPanelOpen(false)
      }
    }

    handleViewportChange()
    mediaQuery.addEventListener('change', handleViewportChange)

    return () => mediaQuery.removeEventListener('change', handleViewportChange)
  }, [])

  const isSidebarExpanded = isDesktopViewport ? isDesktopSidebarExpanded : isMobileSidebarOpen
  const isSidebarVisible = isDesktopViewport ? true : isMobileSidebarOpen
  const isFilterPanelVisible = showFilterControls
    ? isDesktopViewport
      ? isDesktopFilterPanelOpen
      : isMobileFilterPanelOpen
    : false
  const isFilterOnlyVisible = !isDesktopViewport && isMobileFilterPanelOpen
  const sidebarTokens = designTokens.components.videoLibrarySidebar
  const headerHeight = 76
  const sidebarBaseWidth = isSidebarExpanded ? sidebarTokens.expandedWidth : sidebarTokens.railWidth
  const sidebarReservedWidth = isSidebarVisible
    ? isFilterPanelVisible
      ? `calc(${sidebarBaseWidth} + ${sidebarTokens.panelWidth})`
      : sidebarBaseWidth
    : '0px'

  return (
    <section
      className={cn(
        'relative flex w-full flex-col overflow-clip',
        fullHeight ? 'h-screen' : 'min-h-screen',
        className
      )}
      style={{ backgroundColor: designTokens.colors.neutral.black }}
    >
      <VideosShellHeader
        isFilterPanelOpen={isFilterPanelVisible}
        isSidebarExpanded={isSidebarExpanded}
        isDesktopViewport={isDesktopViewport}
        showFilterControl={showFilterControls}
        showSearchControl={showSearchControl}
        searchValue={searchValue}
        onSearchValueChange={onSearchValueChange}
        onMenuToggle={() => {
          if (isDesktopViewport) {
            setIsDesktopSidebarExpanded((expanded) => !expanded)
            return
          }

          setIsMobileFilterPanelOpen(false)
          setIsMobileSidebarOpen((open) => !open)
        }}
        onFilterClick={() => {
          if (isDesktopViewport) {
            setIsDesktopFilterPanelOpen((open) => !open)
            return
          }

          setIsMobileSidebarOpen(false)
          setIsMobileFilterPanelOpen((open) => !open)
        }}
        onLogout={onLogout}
      />
      <div
        className="relative z-10 flex w-full flex-1 min-h-0"
        style={{ paddingTop: `${headerHeight}px` }}
      >
        {isFilterOnlyVisible ? (
          <div
            aria-hidden="true"
            data-testid="videos-shell-filter-backdrop"
            className="fixed inset-x-0 bottom-0 z-20 lg:hidden"
            style={{ top: `${headerHeight}px` }}
            onMouseDown={() => setIsMobileFilterPanelOpen(false)}
            onTouchStart={() => setIsMobileFilterPanelOpen(false)}
          />
        ) : null}
        <VideosShellSidebar
          isDesktopViewport={isDesktopViewport}
          isVisible={isSidebarVisible}
          isExpanded={isSidebarExpanded}
          isFilterPanelOpen={isFilterPanelVisible}
          isFilterOnlyVisible={isFilterOnlyVisible}
          showFilterPanel={showFilterControls}
          activeNavigationItem={activeNavigationItem}
          onNavigationSelect={onNavigationSelect}
          currentPathname={currentPathname}
          shortcuts={shortcuts}
          categories={categories}
          selectedCategorySlug={selectedCategorySlug}
          onCategorySelect={onCategorySelect}
          bundles={bundles}
          selectedBundleId={selectedBundleId}
          onBundleSelect={onBundleSelect}
          filterSections={filterSections}
          onFilterSelectionChange={onFilterSelectionChange}
          onResetFilters={onResetFilters}
          onLogout={onLogout}
        />
        <div className="flex w-full flex-1 min-h-0">
          <div className="hidden shrink-0 lg:block" style={{ width: sidebarReservedWidth }} />
          <div className="flex min-h-0 min-w-0 flex-1">{children}</div>
        </div>
      </div>
    </section>
  )
}

VideosShell.displayName = 'VideosShell'
